<template>
	<div class="flex flex-wrap items-start pt-2 md:px-3 md:flex-nowrap">
		<div class="sticky z-20 w-full mb-2 top-22 md:w-64 menu-controls">
			<!-- CART & FILTER BUTTON -->
			<div
				v-if="isMounted"
				class="flex flex-wrap md:pt-4"
			>
				<div
					v-if="cart.settings.hasOrdering === true"
					class="flex-col w-full py-0 text-sm font-bold md:py-2 md:flex"
				>
					<div class="py-1.5 hidden md:block">
						YOUR CART
					</div>
					<BusinessMenuCart
						:business-api-param="businessApiParam"
						:cart="cart"
						:subtotal="subtotal"
						class="w-full bg-white"
					/>
				</div>
				<div class="w-full py-2">
					<div class="text-sm font-bold py-1.5 hidden md:block bg-white min-h-8">
						{{ itemCount }} PRODUCTS
					</div>
					<div class="xs:force-w-screen sm:force-w-screen md:w-full md:ml-0 md:mr-0">
						<div class="flex w-full px-3 py-2 space-x-2 bg-white md:py-0 md:px-0">
							<div class="relative flex content-center w-full">
								<WwTextInput
									id="search-query"
									v-model="searchQuery"
									class="w-full"
									border-class="border"
									input-class="self-center w-full h-10 bg-white border rounded-md shadow-sm md:h-12 hover:shadow pl-9 focus:ring focus:ring-green-300 focus:border-gray-300"
									placeholder="Search in Menu..."
								/>
								<MagnifierIcon
									height="15"
									width="15"
									class="absolute transform -translate-x-1/2 -translate-y-1/2 top-1/2 left-5"
								/>
							</div>
							<div class="flex items-center justify-end md:hidden">
								<button
									class="items-center justify-center w-10 h-10 bg-white border border-gray-300 rounded-full shadow-sm hover:shadow"
									@click="showModal(MENU_FILTER_MODAL)"
								>
									<img
										src="@/assets/icons/filters-circle.svg"
										class="h-full rounded-full"
										height="40"
										width="40"
										alt="Filters"
									>
								</button>
							</div>
						</div>
					</div>
				</div>
			</div>

			<!-- MED/REC SPLIT & LIST/GRID VIEW -->
			<div
				v-if="business.indication == 'both'"
				class="hidden w-full py-2 md:block"
			>
				<div class="text-sm font-bold py-1.5">
					MENU TYPE
				</div>
				<WwLabeledSwitch
					ref="switch"
					:options="indicationOptions"
					:width="14"
					:initial-option="activeIndicationObject"
					button-classes="shadow-sm group-hover:shadow"
					@selection-changed="updateIndication"
				/>
			</div>
			<div class="hidden w-full py-2 md:block">
				<div class="text-sm font-bold py-1.5">
					SORT BY
				</div>
				<select
					v-model="sortSelected"
					:options="sortOptions"
					class="w-full h-12 bg-white border-gray-300 rounded-md shadow-sm focus:ring focus:ring-green-300 focus:border-gray-300 hover:shadow"
				>
					<option
						v-for="option in sortOptions"
						:key="option.value"
						:value="option.value"
					>
						{{ option.text }}
					</option>
				</select>
			</div>

			<!-- FILTERS -->
			<div class="hidden w-full py-2 md:block">
				<div class="text-sm font-bold py-1.5">
					CATEGORIES
				</div>
				<BusinessMenuFilters
					ref="filters"
					:menu-filters="menuFilters"
					:active-filters="activeFilters"
					:initial-filter="initialFilter"
					@set-filters="setFilters"
				/>
			</div>
			<button
				class="text-sm font-semibold bg-white p-2.5 border border-gray-300 rounded shadow-sm hover:shadow hidden md:block"
				@click="resetFilters"
			>
				RESET FILTERS
			</button>
		</div>
		<div class="flex-1">
			<template v-if="isLoading">
				<PlaceholderCard />
				<PlaceholderCard />
				<PlaceholderCard />
				<PlaceholderCard />
			</template>

			<div
				v-if="!isLoading && filteredMenu.length"
				class="flex flex-col w-full divide-y divide-gray-200 divide-solid"
			>
				<ProductCategory
					v-for="(menuSet, index) in filteredMenu"
					:key="index"
					:category="categories[menuSet.category]"
					:product-ids="menuSet.products"
					:products="products"
					:indication="indication"
					:is-indication-split="isIndicationSplit"
				/>
			</div>
			<div
				v-if="!isLoading && !filteredMenu.length"
				class="empty-search-transition"
			>
				<transition
					appear
					name="fade"
				>
					<p class="text-center">
						Your search returned no results.
					</p>
				</transition>
			</div>
		</div>
		<BusinessMenuFilterModal
			:business="business"
			:menu-filters="menuFilters"
			:active-filters="activeFilters"
			:active-indication="activeIndicationObject"
			:initial-filter="initialFilter"
			:cart="cart"
			:active-sort="sortSelected"
			@set-filters="setFilters"
			@emit-sort-selected="sort => sortSelected = sort"
			@emit-product-view="view => productView = view"
			@update-indication="updateIndication"
		/>
	</div>
</template>

<script>
import {
	mapGetters,
	mapMutations,
	mapState
} from 'vuex'

import BusinessMenuCart from '@/components/business/BusinessMenuCart.vue'
import MagnifierIcon from '@/components/icons/MagnifierIcon.vue'
import PlaceholderCard from '@/components/multiUse/PlaceholderCard'
import WwLabeledSwitch from '@/components/multiUse/WwLabeledSwitch.vue'
import ProductCategory from '@/components/product/ProductCategory.vue'
import WwTextInput from '@/components/UI/WwTextInput.vue'
import { INDICATION_TYPES } from '@/constants/business/index.js'
import {
	ADD_TO_CART_MODAL,
	MENU_FILTER_MODAL
} from '@/constants/modal/names.js'
import menuFilters from '@/etc/generated/menuFilters.json'
import {
	sortPriceHigh,
	sortPriceLow
} from '@/utils/sort.js'
const { MEDICAL, RECREATIONAL } = INDICATION_TYPES

export default {
	components: {
		PlaceholderCard,
		ProductCategory,
		BusinessMenuCart,
		BusinessMenuFilters: () => import('@/components/business/BusinessMenuFilters.vue'),
		BusinessMenuFilterModal: () => import('@/components/UI/modals/BusinessMenuFilterModal.vue'),
		MagnifierIcon,
		WwLabeledSwitch,
		WwTextInput
	},
	props: {
		initialFilter: {
			type: String,
			default: ''
		}
	},
	data() {
		return {
			MENU_FILTER_MODAL,
			indication: 'recreational',
			searchQuery: '',
			activeFilter: this.initialFilter,
			activeFilters: [],
			sortSelected: null,
			sortOptions: [
				{ value: null, text: 'Sort' },
				{ value: 'sortPriceHigh', text: 'Highest Price' },
				{ value: 'sortPriceLow', text: 'Lowest Price' }
			],
			isMounted: false,
			indicationOptions: [
				RECREATIONAL,
				MEDICAL
			]
		}
	},
	computed: {
		...mapState('modal', [
			'isOpen',
			'activeModalId'
		]),
		toastSecondaryText() {
			let text = `${this.cart.lastAddedItem.name}`
			if (this.cart.lastAddedItem.quantity > 1) {
				text = text.concat(` (x${this.cart.lastAddedItem.quantity})`)
			}
			return text
		},
		itemCount() {
			let totalCount = 0
			if (this.filteredMenu.length < 1) return totalCount
			this.filteredMenu.forEach((category) => {
				totalCount += category.products.length
			})
			return totalCount
		},
		filteredMenu() {
			const tempMenu = []
			if (!this.menu?.length) return tempMenu
			this.menu.forEach((category) => {
				const tempCategory = { ...category }
				tempCategory.products = tempCategory.products.filter(productId => {
					const tempProduct = this.products[productId]
					return this.checkIndication(tempProduct) &&
						this.checkFilter(tempProduct) &&
						this.checkQuery(tempProduct)
				})
				if (this.sortSelected !== null) { // this calls the various sort methods defined below depending on which one is selected
					tempCategory.products.sort(this[this.sortSelected])
				}
				tempMenu.push(tempCategory)
			})
			return tempMenu
		},
		subtotal() {
			if (this.cart.subtotal !== undefined) {
				return this.cart.subtotal.toFixed(2)
			} else {
				return 0
			}
		},
		...mapGetters('business',
			[ 'business', 'menu', 'products', 'photos', 'categories', 'businessApiParam', 'isLoading' ]
		),
		...mapGetters('auth',
			[ 'auth' ]
		),
		...mapGetters('cart',
			[ 'cart' ]
		),
		...mapGetters([ 'appParams' ]),
		menuFilters() {
			return {
				ids: menuFilters.ids.filter((menuFilter) => { // filter the ids of what filters are going to be available based on which categories are active in the menu
					let tempCheck = false
					menuFilters.items[menuFilter].category_types.forEach((categoryType) => {
						for (const categoryId in this.categories) {
							if (this.categories[categoryId].type == categoryType) {
								tempCheck = true
								return
							}
						}
					})
					return tempCheck
				}),
				items: menuFilters.items
			}
		},
		isIndicationSplit() {
			return !!this.business?.indication && !!this.business?.indication === 'both'
		},
		activeIndicationObject() {
			return this.indication === 'recreational' ? RECREATIONAL : MEDICAL
		}
	},
	watch: {
		initialFilter: {
			immediate: true,
			handler(newVal) {
				if (newVal) { this.activeFilters.push(newVal) }
			}
		},
		menu: {
			immediate: true,
			handler() {
				if (this.business.indication !== 'both') {
					this.indication = this.business.indication
				}
			}
		},
		'cart.lastAddedItem'(item, previousItem) {
			if (item === previousItem || !this.cart.lastAddedItem.name) return
			this.showModal(ADD_TO_CART_MODAL)
		}
	},
	mounted() {
		this.isMounted = true
	},
	methods: {
		...mapMutations('modal', [ 'showModal' ]),
		...mapMutations('toast', [ 'showToast' ]),
		checkIndication(product) {
			return product[this.indication]
		},
		checkFilter(product) {
			return (!this.activeFilters.length ? true : this.validateFilterItems(menuFilters, product))
		},
		validateFilterItems(menuFilters, product) {
			for (let i = 0; i < this.activeFilters.length; i++) {
				if (menuFilters.items[this.activeFilters[i]].category_types.indexOf(product.sub_type) > -1) {
					return true
				}
			}
		},
		checkQuery(product) { // return true if there is no searchQuery else compare
			return (this.searchQuery == '' ? true : product.name.toLowerCase().includes(this.searchQuery.toLowerCase()))
		},
		sortPriceHigh(a, b) {
			return sortPriceHigh({
				a,
				b,
				products: this.products,
				indication: this.indication
			})
		},
		sortPriceLow(a, b) {
			return sortPriceLow({
				a,
				b,
				products: this.products,
				indication: this.indication
			})
		},
		setFilters(filters) {
			this.activeFilters = filters
		},
		updateIndication(indication) {
			this.indication = indication
		},
		resetFilters() {
			this.$refs.filters.clear()
			this.sortSelected = null
			this.searchQuery = ''
			if (this.indication === MEDICAL.value) {
				this.$refs.switch.handleSelectionUpdated()
			}
		}
	}
}
</script>
<style lang="scss" scoped>

$menu-sticky-top-offset: -68px;

.empty-search-transition {
	.fade-enter-active {
		transition: all 0.35s ease-out;
		transition-delay: 0.13s
	}
	.fade-leave-active {
		transition: all 0.35s ease-out;
	}
	.fade-enter,
	.fade-leave-to {
		opacity: 0;
		transform: translateY(100px);
	}
}
.menu-controls {
	@apply bg-none
}

@media (min-width: $break-md) {
	.scrolled-past .menu-controls {
		padding-top: $menu-sticky-top-offset;
	}
}

.top-22 {
	top: 5.5rem;
}
</style>
